import invariant from "tiny-invariant"
import type { Address } from "viem"
import { type SupportedChainId, isSupportedChainId } from "../config"
import type { Token } from "./Token"
import type { TokenType } from "./types"

/**
 * Interface for the constructor arguments of the Currency class.
 */
interface CurrencyArgs {
  chainId?: number | string | undefined
  decimals?: number | string | undefined
  symbol?: string | undefined
  name?: string | undefined
}

/**
 * The `Currency` class serves as an abstract base for representing different types of currencies
 * within the application. It provides foundational attributes common to all currencies, such as
 * `chainId` (to denote which blockchain the currency belongs to), `decimals` (indicating the granularity
 * of the currency), and optional attributes like `symbol` and `name` for more descriptive purposes.
 *
 * The design includes abstract flags (`isNative` and `isToken`) to allow derived classes to specify
 * the type of currency, i.e., whether it's a native blockchain currency like ETH, or a token-based
 * currency like an ERC20 token.
 *
 * The abstract nature of the `Currency` class ensures that concrete implementations (like `Token` or `Native`)
 * provide specific logic and attributes tailored to their kind, while still adhering to a consistent
 * interface. This design promotes type safety, code reusability, and a hierarchical understanding
 * of currencies within the ecosystem.
 *
 * Fundamental operations such as equality checking (`equals`) and obtaining a wrapped version of the
 * currency (`wrapped`) are defined abstractly, ensuring that derived classes provide specific implementations.
 *
 * The class is integral to financial or transactional operations, ensuring that values are associated
 * with a specific and clear context of currency, aiding in preventing common financial errors
 * (like unit mismatches) and bringing clarity to transactional logic.
 */
export abstract class Currency {
  /**
   * Flag to indicate if the currency is native (e.g., ETH).
   * @type {boolean}
   */
  abstract readonly isNative: boolean

  /**
   * Flag to indicate if the currency is a token (e.g., ERC20).
   * @type {boolean}
   */
  abstract readonly isToken: boolean

  /**
   * The contract address on the blockchain where this token is deployed.
   * @type {Address}
   */
  abstract readonly address: Address

  /**
   * Identifier for the blockchain.
   * @type {SupportedChainId}
   */
  readonly chainId?: SupportedChainId | undefined

  /**
   * Number of decimal places the currency uses.
   * @type {number}
   */
  readonly decimals: number

  /**
   * Symbol of the currency, e.g., 'ETH'.
   * @type {string | undefined}
   */
  readonly symbol?: string | undefined

  /**
   * Name of the currency, e.g., 'Ethereum'.
   * @type {string | undefined}
   */
  readonly name?: string | undefined

  /**
   * Constructs a Currency instance.
   * @param {CurrencyArgs} args The constructor arguments.
   */
  protected constructor({
    chainId: _chainId,
    decimals: _decimals = 0,
    symbol,
    name,
  }: CurrencyArgs) {
    const decimals = Number(_decimals)
    invariant(
      decimals >= 0 && decimals < 255 && Number.isInteger(decimals),
      "DECIMALS",
    )

    if (_chainId) {
      const chainId = Number(_chainId)
      invariant(Number.isSafeInteger(chainId), "CHAIN_ID")
      invariant(isSupportedChainId(chainId), `UNSUPPORTED_CHAIN_ID_${chainId}`)
      this.chainId = chainId
    }

    this.decimals = decimals
    this.symbol = symbol
    this.name = name
  }

  /**
   * Checks if the current currency equals another currency.
   * @param other The other currency type to compare with.
   * @returns {boolean} True if both currencies are equal, false otherwise.
   */
  abstract equals(other: TokenType): boolean

  /**
   * Returns the wrapped version of this currency.
   * @returns {Token} The wrapped representation of the currency.
   */
  abstract get wrapped(): Token
}
